import './App.css';
import siteConfig from './siteConfig';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/contexts/AuthContext'; // Updated import
import { GlobalContext } from './components/contexts/GlobalContext';
import { useCallback, useRef, useState, useEffect, Suspense, lazy } from 'react';
import axios from 'axios';

import { SocketProvider } from './components/contexts/SocketContext'; // Updated path
import { WatiSocketProvider } from './components/contexts/WatiSocketContext';
import { WatiChatProvider } from './components/contexts/WatiChatContext';
import AppSplash from './components/AppSplash';
import { Modal, Button, App as AntApp } from 'antd';
import DrawerNew from './components/DrawerNew';
import GetTitle from './components/GetTitle';
import { handleDismissibleNotificationClick } from './utils/dismissibleNotifications';
import { InternalChatProvider } from './components/internalChat/InternalChatContext';
import { useSocket } from './components/contexts/SocketContext';
import { API_DOMAIN } from './components/constants';
import { countSrmChangelogItems, isSrmUpdateNewer, srmUpdateIdentity, srmUpdateRequiresLogout } from './utils/srmVersion';
import { AccessProvider, useAccess } from './features/access/context/AccessProvider';
import RequirePermission from './features/access/components/RequirePermission';
import AccessDenied from './features/access/components/AccessDenied';
import { firstAccessiblePath, PERMISSIONS } from './features/access/constants/permissionKeys';

// Import Pages — Login stays eager (first paint for logged-out users);
// everything else is code-split so the initial bundle stays lean.
import Login from './pages/Login';
import NotFound from './components/Modals/NotFound';

// Stable reference — passing a fresh object literal here on every App
// re-render (App re-renders often, since GlobalContext's own value prop is
// also a fresh object every time) made antd's <App> treat its notification
// config as "changed" on every render, tearing down and recreating the
// entire notification holder mid-display. That explained late-appearing,
// almost-instantly-dismissed notifications landing in the wrong corner
// (a freshly recreated holder falling back to its default placement before
// ever applying a call's actual placement).
const ANT_APP_NOTIFICATION_CONFIG = { stack: false };

const DashboardV2      = lazy(() => import('./pages/DashboardV2'));
const Users            = lazy(() => import('./pages/Users'));
const QuerysetV4       = lazy(() => import('./components/qv4/views/page/QuerysetV4'));
const OperationsV2     = lazy(() => import('./components/operations-v2/views/page/OperationsV2'));
const PaymentsAccounts = lazy(() => import('./components/payments-v2/views/accounts/PaymentsAccountsPage'));
const WhatsApp         = lazy(() => import('./pages/WhatsApp'));
const WhatsappMessages = lazy(() => import('./pages/WhatsappMessages'));
const Supplier         = lazy(() => import('./pages/Supplier'));
const Hrm              = lazy(() => import('./pages/HRM'));
const Reports          = lazy(() => import('./pages/Reports'));
const OverviewReport   = lazy(() => import('./pages/Reports/Overview'));
const PriceSearch      = lazy(() => import('./pages/PriceSearch'));
const CustomerManagement = lazy(() => import('./pages/CustomerManagement'));
const AIAssistant      = lazy(() => import('./pages/AIAssistant'));
const ViewsPreview     = lazy(() => import('./pages/ViewsPreview'));

const Protected = ({ permission, children }) => <RequirePermission permission={permission}>{children}</RequirePermission>;
const AccessibleHome = () => {
  const { status, can } = useAccess();
  if (status === 'loading' || status === 'idle') return <AppSplash />;
  return <Navigate to={firstAccessiblePath(can)} replace />;
};

const newVersionBannerKeyframes = `
  @keyframes srm-banner-slidein {
    from { transform: translateY(-100%); opacity: 0; }
    to   { transform: translateY(0);     opacity: 1; }
  }
  @keyframes srm-banner-pulse {
    0%, 100% { box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 0 0 rgba(255,255,255,0.4); }
    50%       { box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 0 0 8px rgba(255,255,255,0); }
  }
  @keyframes srm-rocket-bounce {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-4px); }
  }
`;

const NewVersionBanner = () => {
  const { socket, isConnected } = useSocket();
  const { logout } = useAuth();
  const [show, setShow] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const lastUpdateIdentityRef = useRef(null);

  const processUpdate = useCallback((data) => {
    if (!isSrmUpdateNewer(data, siteConfig)) return;
    const identity = srmUpdateIdentity(data);
    if (identity === lastUpdateIdentityRef.current) return;
    lastUpdateIdentityRef.current = identity;
    setUpdateInfo(data);
    // A later notification replaces the banner's data in place. Receiving
    // updates must never interrupt the user's work by opening a modal.
    setShow(true);
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    socket.on('newVersionAvailable', processUpdate);
    return () => socket.off('newVersionAvailable', processUpdate);
  }, [socket, processUpdate]);

  // Correctness fallback for events missed while this browser was offline.
  // Runs on the initial authenticated connection and every SRM socket recovery;
  // there is deliberately no grace delay.
  useEffect(() => {
    if (!isConnected) return;
    axios.get(`${API_DOMAIN}/whatsapp/latest_srm_update`)
      .then(response => processUpdate(response.data?.data))
      .catch(() => {});
  }, [isConnected, processUpdate]);

  const availableVersion = updateInfo?.version || "Unknown";
  const availableBuild = updateInfo?.build_no;
  const versionSummary = `v${siteConfig.version}${siteConfig.build_no != null ? ` (build ${siteConfig.build_no})` : ""} → v${availableVersion}${availableBuild != null ? ` (build ${availableBuild})` : ""}`;
  const changelog = updateInfo?.changelog || null;
  const parsedChangeCount = Number(updateInfo?.change_count);
  const changeCount = Number.isInteger(parsedChangeCount) && parsedChangeCount > 0
    ? parsedChangeCount
    : countSrmChangelogItems(changelog);
  const requiresLogout = srmUpdateRequiresLogout(updateInfo);

  const loadNewVersion = () => {
    if (requiresLogout) logout();
    window.location.reload(true);
  };

  return (
    <>
      <style>{newVersionBannerKeyframes}</style>
      {show && (
        <div
          onClick={() => setDetailsModalOpen(true)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            background: 'linear-gradient(90deg, #1d4ed8 0%, #2563eb 50%, #1d4ed8 100%)',
            backgroundSize: '200% 100%',
            color: 'white',
            textAlign: 'center', padding: '10px 16px',
            cursor: 'pointer',
            animation: 'srm-banner-slidein 0.4s cubic-bezier(0.22,1,0.36,1) both, srm-banner-pulse 2.5s ease-in-out 0.5s infinite',
            userSelect: 'none',
          }}
        >
          <span style={{ animation: 'srm-rocket-bounce 1.2s ease-in-out infinite', display: 'inline-block', marginRight: 6 }}>🚀</span>
          <span style={{ fontSize: 14, fontWeight: 700 }}>New SRM update available: v{availableVersion}{availableBuild != null ? ` (build ${availableBuild})` : ""}.</span>
          <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.92, marginLeft: 10 }}>
            {changeCount > 0 ? `${changeCount} ${changeCount === 1 ? "update" : "updates"} available.` : "See what's new."} Click to review and load.
          </span>
        </div>
      )}
      <Modal
        open={detailsModalOpen}
        onCancel={() => setDetailsModalOpen(false)}
        closable
        maskClosable
        width="min(900px, 96vw)"
        style={{ top: 12, paddingBottom: 0 }}
        styles={{ content: { height: 'calc(100vh - 24px)', display: 'flex', flexDirection: 'column' }, body: { flex: 1, minHeight: 0, overflowY: 'auto' } }}
        title={`🚀 SRM ${availableVersion} is ready`}
        footer={[
          <Button key="later" onClick={() => setDetailsModalOpen(false)}>Not now</Button>,
          <Button key="refresh" type="primary" onClick={loadNewVersion}>Load new version</Button>,
        ]}
      >
        {changelog
          ? <><div style={{ marginBottom: 8 }}>Installed and available versions: <strong>{versionSummary}</strong>. Refresh now to update.</div><div style={{ background: '#f5f5f5', borderRadius: 6, padding: '8px 12px', fontSize: 13, whiteSpace: 'pre-wrap' }}>{changelog}</div></>
          : <>A newer SRM build is available: <strong>{versionSummary}</strong>. Refresh now to update.</>
        }
        {requiresLogout && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid #f0f0f0', color: '#cf1322', fontSize: 12, fontWeight: 600 }}>
            This update requires you to relogin. Loading the new version will redirect you to the login screen.
          </div>
        )}
      </Modal>
    </>
  );
};

// Create a wrapper component for authenticated routes
const AuthenticatedApp = () => {
  const { authState } = useAuth();

  return (
    <>
      <NewVersionBanner />
      <Routes>
        {!authState.isAuthenticated ? (
        <Route path="*" element={<Login />} />
      ) : (
        <Route path="/" element={<DrawerNew title={<GetTitle />} />}>
          <Route path="/login" element={<AccessibleHome />} />
          <Route index element={<AccessibleHome />} />
          <Route path="/dashboard" element={<Protected permission={PERMISSIONS.DASHBOARD_VIEW}><DashboardV2 /></Protected>} />
          <Route path="/users" element={<Protected permission={PERMISSIONS.USER_APPROVALS_VIEW}><Users /></Protected>} />
          <Route path="/querysetv4" element={<Protected permission={PERMISSIONS.SALES_VIEW}><QuerysetV4 /></Protected>} />
          <Route path="/operations-v2" element={<Protected permission={PERMISSIONS.OPERATIONS_VIEW}><OperationsV2 /></Protected>} />
          <Route path="/operations-v2/orders/:orderId" element={<Protected permission={PERMISSIONS.OPERATIONS_VIEW}><OperationsV2 /></Protected>} />
          <Route path="/payments/accounts" element={<Protected permission={PERMISSIONS.PAYMENTS_VIEW_ACCOUNTS_WORKSPACE}><PaymentsAccounts /></Protected>} />
          <Route path="/whatsapp" element={<Protected permission={PERMISSIONS.WATI_DIAGNOSTICS_VIEW}><WhatsApp /></Protected>} />
          <Route path="/whatsapp_messages" element={<Protected permission={PERMISSIONS.SUPPLIER_CHATS_VIEW}><WhatsappMessages /></Protected>} />
          <Route path="/suppliers" element={<Protected permission={PERMISSIONS.SUPPLIERS_VIEW}><Supplier /></Protected>} />
          <Route path="/hrm" element={<Protected permission={PERMISSIONS.USERS_VIEW}><Hrm /></Protected>} />
          <Route path="/reports" element={<Protected permission={PERMISSIONS.REPORTS_VIEW}><Reports /></Protected>} />
          <Route path="/reports/overview" element={<Protected permission={PERMISSIONS.REPORTS_OVERVIEW_VIEW}><OverviewReport /></Protected>} />
          <Route path="/search_results" element={<Protected permission={PERMISSIONS.PRICE_SEARCH_VIEW}><PriceSearch /></Protected>} />
          <Route path="/price-search" element={<Protected permission={PERMISSIONS.PRICE_SEARCH_VIEW}><PriceSearch /></Protected>} />
          <Route path="/customers" element={<Protected permission={PERMISSIONS.CUSTOMERS_VIEW}><CustomerManagement /></Protected>} />
          <Route path="/ai_assistant" element={<Protected permission={PERMISSIONS.AI_ASSISTANT_VIEW}><AIAssistant /></Protected>} />
          <Route path="/views-preview" element={<Protected permission={PERMISSIONS.DEVELOPER_TOOLS_VIEW}><ViewsPreview /></Protected>} />
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      )}
    </Routes>
    </>
  );
};

function App() {
  const [globalState, setGlobalState] = useState({
    messages: [],
    queryset: [],
    approval_messages: [],
  });

  // Without this, dropping a file anywhere outside a component's own drop
  // zone (including onto a Modal portal, which lives outside any chat
  // panel's own onDrop area) falls through to the browser's default
  // behavior: navigating the whole tab away to display that file. Blocking
  // dragover/drop at the window level stops that everywhere, unconditionally
  // — local onDrop handlers still fire normally since this only prevents the
  // browser's own default action, not React's synthetic event dispatch.
  useEffect(() => {
    const preventDefault = (e) => e.preventDefault();
    window.addEventListener("dragover", preventDefault);
    window.addEventListener("drop", preventDefault);
    return () => {
      window.removeEventListener("dragover", preventDefault);
      window.removeEventListener("drop", preventDefault);
    };
  }, []);

  // Every notification/message toast in the app should be click-to-dismiss.
  // Wired once here rather than at each of the 90+ call sites — see
  // dismissibleNotifications.js for why a single DOM-level listener covers
  // both the static antd API and App.useApp()'s context-scoped instance.
  useEffect(() => {
    const onClick = (e) => handleDismissibleNotificationClick(e.target);
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <GlobalContext.Provider value={{ globalState, setGlobalState }}>
      <AntApp notification={ANT_APP_NOTIFICATION_CONFIG}>
        <AuthProvider>
          <SocketProvider>
            <AccessProvider>
              <InternalChatProvider>
                <WatiSocketProvider>
                  <Router>
                    <WatiChatProvider>
                      <Suspense fallback={<AppSplash />}>
                        <AuthenticatedApp />
                      </Suspense>
                    </WatiChatProvider>
                  </Router>
                </WatiSocketProvider>
              </InternalChatProvider>
            </AccessProvider>
          </SocketProvider>
        </AuthProvider>
      </AntApp>
    </GlobalContext.Provider>
  );
}

export default App;
